import { FURNISHINGS, listingCompleteness, parseMapPoint, type PropertyFacts } from '@sauda/shared';
import { paiseToRupeesInput, rupeesToPaise } from './money.js';
import type { PropertyView } from './types.js';

export type Furnishing = (typeof FURNISHINGS)[number];

/** What the form holds, all as the text a person typed so a half finished form is never lost. */
export interface PropertyDraft {
  kind: 'rent' | 'sale';
  title: string;
  /** `1RK`, `1`, `2`, `3` or `4+`, or empty. */
  bhk: string;
  money: string;
  deposit: string;
  maintenance: string;
  locality: string;
  society: string;
  flatNo: string;
  floor: string;
  carpet: string;
  furnishing: Furnishing | '';
  amenities: string[];
  ownerName: string;
  ownerPhone: string;
  ownerLang: 'en' | 'mr' | 'hi';
  tellOwner: boolean;
  notes: string;
  availableFrom: string;
  /** What the broker tells about the flat; brokerage and map are typed as text and parsed on save. */
  facts: PropertyFacts;
  brokerageText: string;
  mapText: string;
}

export const EMPTY_DRAFT: PropertyDraft = {
  kind: 'rent',
  title: '',
  bhk: '',
  money: '',
  deposit: '',
  maintenance: '',
  locality: '',
  society: '',
  flatNo: '',
  floor: '',
  carpet: '',
  furnishing: '',
  amenities: [],
  ownerName: '',
  ownerPhone: '',
  ownerLang: 'en',
  tellOwner: true,
  notes: '',
  availableFrom: '',
  facts: {},
  brokerageText: '',
  mapText: '',
};

/** The BHK chips. `1RK` is stored as 0, `4+` as 4. */
export const BHK_CHOICES = ['1RK', '1', '2', '3', '4+'] as const;

export function bhkToNumber(choice: string): number | null {
  if (choice === '') return null;
  if (choice === '1RK') return 0;
  if (choice === '4+') return 4;
  return Number(choice);
}

export function bhkToChoice(bhk: number | null): string {
  if (bhk === null) return '';
  return bhk === 0 ? '1RK' : bhk >= 4 ? '4+' : String(bhk);
}

export const AMENITY_CHOICES = ['Security', 'Gym', 'Garden', 'Balcony'] as const;

export function draftFromView(view: PropertyView): PropertyDraft {
  return {
    kind: view.kind,
    title: view.title,
    bhk: bhkToChoice(view.bhk),
    money: paiseToRupeesInput(view.kind === 'rent' ? view.rentPaise : view.pricePaise),
    deposit: paiseToRupeesInput(view.depositPaise),
    maintenance: paiseToRupeesInput(view.maintenancePaise),
    locality: view.locality ?? '',
    society: view.society ?? '',
    flatNo: view.flatNo ?? '',
    floor: view.floor ?? '',
    carpet: view.carpetSqft === null ? '' : String(view.carpetSqft),
    furnishing: (view.furnishing as Furnishing | null) ?? '',
    amenities: view.amenities,
    ownerName: view.ownerName ?? '',
    ownerPhone: view.ownerPhone ?? '',
    ownerLang: view.ownerLang ?? 'en',
    tellOwner: view.tellOwner ?? true,
    notes: view.notes ?? '',
    availableFrom: view.availableFrom ?? '',
    facts: view.facts ?? {},
    brokerageText: brokerageToText(view.facts?.brokerage),
    mapText:
      view.facts?.location?.latitude != null && view.facts.location.longitude != null
        ? `${String(view.facts.location.latitude)},${String(view.facts.location.longitude)}`
        : '',
  };
}

function brokerageToText(b: PropertyFacts['brokerage']): string {
  if (b == null) return '';
  return b.kind === 'amount' ? paiseToRupeesInput(b.amountPaise ?? null) : String(b.months ?? '');
}

/** The facts as the API takes them: the typed brokerage and map link are read here. */
export function factsFromDraft(draft: PropertyDraft): PropertyFacts {
  const facts: PropertyFacts = { ...draft.facts };
  const b = facts.brokerage;
  if (b != null) {
    const text = draft.brokerageText.trim();
    if (b.kind === 'amount') {
      facts.brokerage = {
        kind: 'amount',
        amountPaise: text === '' ? null : rupeesToPaise(text),
        payer: b.payer,
      };
    } else {
      const months = Number(text);
      facts.brokerage = {
        kind: 'months',
        months: text === '' || Number.isNaN(months) ? null : months,
        payer: b.payer,
      };
    }
  }
  const point = draft.mapText.trim() === '' ? null : parseMapPoint(draft.mapText);
  const location = { ...facts.location };
  if (draft.mapText.trim() === '') {
    delete location.latitude;
    delete location.longitude;
  } else if (point !== null) {
    location.latitude = point.latitude;
    location.longitude = point.longitude;
  }
  if (Object.keys(location).length > 0) facts.location = location;
  else delete facts.location;
  return facts;
}

/** "8 of 14 details filled", from what the form holds right now. */
export function draftCompleteness(draft: PropertyDraft) {
  return listingCompleteness({
    furnishing: draft.furnishing === '' ? null : draft.furnishing,
    availableFrom: draft.availableFrom === '' ? null : draft.availableFrom,
    floor: draft.floor.trim() === '' ? null : draft.floor.trim(),
    facts: factsFromDraft(draft),
  });
}

/** True when the pasted text is not empty but no point can be read from it. */
export function mapTextProblem(draft: PropertyDraft): boolean {
  return draft.mapText.trim() !== '' && parseMapPoint(draft.mapText) === null;
}

export type BasicsProblems = Partial<Record<'title' | 'money' | 'deposit' | 'maintenance', string>>;

/** What is wrong on the first screen of facts: a title, an amount, and amounts that parse. */
export function basicsProblems(draft: PropertyDraft): BasicsProblems {
  const problems: BasicsProblems = {};
  if (draft.title.trim().length < 2) problems.title = 'title';
  if (rupeesToPaise(draft.money) === null) problems.money = 'money';
  if (draft.deposit.trim() !== '' && rupeesToPaise(draft.deposit) === null)
    problems.deposit = 'amount';
  if (draft.maintenance.trim() !== '' && rupeesToPaise(draft.maintenance) === null)
    problems.maintenance = 'amount';
  return problems;
}

const blank = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** The request body for the API. Empty text becomes null so a cleared field is cleared. */
export function draftToBody(draft: PropertyDraft) {
  const amount = rupeesToPaise(draft.money);
  const carpet = draft.carpet.replace(/\D/g, '');
  return {
    kind: draft.kind,
    title: draft.title.trim(),
    bhk: bhkToNumber(draft.bhk),
    locality: blank(draft.locality),
    society: blank(draft.society),
    flatNo: blank(draft.flatNo),
    floor: blank(draft.floor),
    carpetSqft: carpet === '' ? null : Number(carpet),
    furnishing: draft.furnishing === '' ? null : draft.furnishing,
    rentPaise: draft.kind === 'rent' ? amount : null,
    pricePaise: draft.kind === 'sale' ? amount : null,
    depositPaise: draft.deposit.trim() === '' ? null : rupeesToPaise(draft.deposit),
    maintenancePaise: draft.maintenance.trim() === '' ? null : rupeesToPaise(draft.maintenance),
    amenities: draft.amenities,
    ownerName: blank(draft.ownerName),
    ownerPhone: blank(draft.ownerPhone),
    ownerLang: draft.ownerLang,
    tellOwner: draft.tellOwner,
    notes: blank(draft.notes),
    availableFrom: draft.availableFrom === '' ? null : draft.availableFrom,
    facts: factsFromDraft(draft),
  };
}
